'use strict';

import type {
  DOMConversionMap,
  DOMExportOutput,
  EditorConfig,
  LexicalEditor,
  NodeKey,
  SerializedElementNode,
  Spread,
} from 'lexical';
import { ElementNode, isHTMLElement, $applyNodeReplacement } from 'lexical';
import { addClassNamesToElement } from '@lexical/utils';
import { $createDeleteNode } from './PhxDeleteNode';
import { v4 as uuidv4 } from 'uuid';
import dayjs from 'dayjs';
import { getCurrentShowChangesState } from '../context/SharedTrackChangesContext';

function formatDate(date: any) {
  return dayjs(date).toISOString();
}

/** @module @lexical/link */
/** @noInheritDoc */

export type SerializedInsertNode = Spread<{ username: string; id: string; date: string }, SerializedElementNode>;

export class PhxInsertNode extends ElementNode {
  /** @internal */
  __username: string;
  __id: string;
  __date: string;

  static getType(): string {
    return 'insert';
  }

  static clone(node: PhxInsertNode): PhxInsertNode {
    return new PhxInsertNode(node.__username, node.__id, node.__date, node.__key);
  }

  constructor(username: string, id?: string, date?: string, key?: NodeKey) {
    super(key);
    this.__username = username;
    if (id === null || id === undefined) {
      this.__id = uuidv4();
    } else {
      this.__id = id;
    }

    if (!date) {
      this.__date = formatDate(new Date());
    } else {
      this.__date = date;
    }
  }

  createDOM(config: EditorConfig): HTMLSpanElement {
    const isEnabled = getCurrentShowChangesState();
    const element = document.createElement('span');
    element.dataset.username = this.__username;
    element.setAttribute('id', this.__id);
    element.dataset.createdAt = this.__date;
    const className = isEnabled ? config.theme.insert : config.theme.insertDisabled;
    addClassNamesToElement(element, className);
    return element;
  }

  updateDOM(prevNode: this, dom: HTMLElement, config: EditorConfig): boolean {
    // Note: Don't call this.getWritable() here as updateDOM is called during DOM reconciliation
    // and modifying the node state here causes "_cachedNodes" read-only errors
    // The date is already set when the node is created or modified through proper editor updates
    return false;
  }

  static importDOM(): DOMConversionMap | null {
    return {
      span: (node: Node) => ({
        conversion: convertSpanElement,
        priority: 1,
      }),
    };
  }

  exportDOM(editor: LexicalEditor): DOMExportOutput {
    const { element } = super.exportDOM(editor);

    if (element && isHTMLElement(element)) {
      if (this.isEmpty()) element.append(document.createElement('br'));

      const formatType = this.getFormatType();
      element.style.textAlign = formatType;

      const direction = this.getDirection();
      if (direction) {
        element.dir = direction;
      }
      const indent = this.getIndent();
      if (indent > 0) {
        // padding-inline-start is not widely supported in email HTML, but
        // Lexical Reconciler uses padding-inline-start. Using text-indent instead.
        element.style.textIndent = `${indent * 20}px`;
      }

      element.setAttribute('date', this.__date);
      element.setAttribute('id', this.__id);
      element.setAttribute('data-username', this.__username);
      element.setAttribute('data-created-at', this.__date);

      const className = getCurrentShowChangesState()
        ? editor._config.theme.insert
        : editor._config.theme.insertDisabled;

      element.className = className;
    }

    return {
      element,
    };
  }

  static importJSON(serializedNode: SerializedInsertNode): PhxInsertNode {
    const node = $createInsertNode(serializedNode.username, serializedNode.id, serializedNode.date);
    node.setFormat(serializedNode.format);
    node.setIndent(serializedNode.indent);
    node.setDirection(serializedNode.direction);
    return node;
  }

  exportJSON() {
    return {
      ...super.exportJSON(),
      type: 'insert',
    };
  }

  canContainText(): boolean {
    return true;
  }

  canInsertTextBefore() {
    return true;
  }

  canInsertTextAfter() {
    return true;
  }

  canBeEmpty() {
    return false;
  }

  isInline() {
    return true;
  }

  isTextEntity() {
    return true;
  }

  setNewDate() {
    const writeSelf = this.getWritable();
    writeSelf.__date = formatDate(new Date());
  }
}

function convertSpanElement(domNode: Node) {
  let node = null;
  if (isHTMLElement(domNode)) {
    const className = domNode.className;
    const content = domNode.textContent;
    if (content !== null && content !== '' && className?.includes('editor-insert')) {
      node = $createInsertNode(
        domNode.getAttribute('username')!,
        domNode.getAttribute('id')!,
        domNode.getAttribute('date')!
      );
    }

    if (content !== null && content !== '' && className?.includes('editor-delete')) {
      node = $createDeleteNode(
        domNode.getAttribute('username')!,
        domNode.getAttribute('id')!,
        domNode.getAttribute('date')!
      );
    }
  }

  return {
    node,
  };
}

/**
 * Creates a InsertNode.
 * @returns InsertNode.
 */
export function $createInsertNode(username: string, id?: string, date?: string): PhxInsertNode {
  return $applyNodeReplacement(new PhxInsertNode(username, id, date));
}

/**
 * Determines if node is a InsertNode.
 * @param node - The node to be checked.
 * @returns true if node is a InsertNode, false otherwise.
 */
export function $isInsertNode(node: Node) {
  return node instanceof PhxInsertNode;
}
